import { useSyncExternalStore } from 'react';
import { extraTranslations, type ExtraLang } from './i18n-extra';
import { literalTranslations } from './i18n-literal';

export type Lang = 'zh' | 'zhTW' | 'en' | ExtraLang;

export interface LanguageOption {
  code: Lang;
  label: string;
  htmlLang: string;
  group: 'chinese' | 'eastAsia' | 'global' | 'europe';
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'zh', label: '简体中文', htmlLang: 'zh-CN', group: 'chinese' },
  { code: 'zhTW', label: '繁體中文', htmlLang: 'zh-TW', group: 'chinese' },
  { code: 'en', label: 'English', htmlLang: 'en', group: 'global' },
  { code: 'ja', label: '日本語', htmlLang: 'ja', group: 'eastAsia' },
  { code: 'ko', label: '한국어', htmlLang: 'ko', group: 'eastAsia' },
  { code: 'es', label: 'Español', htmlLang: 'es', group: 'global' },
  { code: 'ptBR', label: 'Português (Brasil)', htmlLang: 'pt-BR', group: 'global' },
  { code: 'fr', label: 'Français', htmlLang: 'fr', group: 'europe' },
  { code: 'de', label: 'Deutsch', htmlLang: 'de', group: 'europe' },
];

const KEY = 'novax:lang';

const listeners = new Set<() => void>();

function isLang(value: string | null): value is Lang {
  return LANGUAGE_OPTIONS.some((option) => option.code === value);
}

function browserDefaultLang(): Lang {
  const locale = typeof navigator === 'undefined' ? '' : navigator.language.toLowerCase();
  if (locale.startsWith('zh-tw') || locale.startsWith('zh-hk') || locale.startsWith('zh-mo')) return 'zhTW';
  if (locale.startsWith('zh')) return 'zh';
  if (locale.startsWith('ja')) return 'ja';
  if (locale.startsWith('ko')) return 'ko';
  if (locale.startsWith('es')) return 'es';
  if (locale.startsWith('pt')) return 'ptBR';
  if (locale.startsWith('fr')) return 'fr';
  if (locale.startsWith('de')) return 'de';
  return locale.startsWith('en') ? 'en' : 'zh';
}

function syncDocumentLanguage(lang: Lang): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = LANGUAGE_OPTIONS.find((option) => option.code === lang)?.htmlLang || 'zh-CN';
}

function getLang(): Lang {
  const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(KEY);
  const lang = isLang(stored) ? stored : browserDefaultLang();
  syncDocumentLanguage(lang);
  return lang;
}

export function getCurrentLang(): Lang {
  return getLang();
}

export function getCurrentLocale(): string {
  const lang = getLang();
  return LANGUAGE_OPTIONS.find((option) => option.code === lang)?.htmlLang || 'zh-CN';
}

export function setLang(lang: Lang) {
  localStorage.setItem(KEY, lang);
  syncDocumentLanguage(lang);
  listeners.forEach((l) => l());
}

export function useLang(): [Lang, (l: Lang) => void] {
  const lang = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    getLang,
  );
  return [lang, setLang];
}

/**
 * Localize legacy UI copy by its canonical Simplified Chinese source text.
 * New UI should prefer semantic keys through useT(); this bridge lets older,
 * text-heavy screens migrate without translating user-provided values.
 */
export function uiText(source: string): string {
  const lang = getLang();
  if (lang === 'zh') return source;
  return literalTranslations[lang]?.[source] || source;
}

export const translations: Record<string, { zh: string; en: string; zhTW: string }> = {
  // Nav
  'nav.memory': { zh: '知识库', en: 'Memory', zhTW: '知識庫' },
  'nav.chat': { zh: '对话', en: 'Chat', zhTW: '對話' },
  'nav.contacts': { zh: '团队', en: 'Contacts', zhTW: '團隊' },
  'nav.skillMarket': { zh: 'Skill 市场', en: 'Skill Market', zhTW: 'Skill 市場' },
  'nav.settings': { zh: '设置', en: 'Settings', zhTW: '設定' },
  'nav.meetings': { zh: '会议室', en: 'Meetings', zhTW: '會議室' },
  'nav.briefings': { zh: '简报', en: 'Briefings', zhTW: '簡報' },
  'nav.ontology': { zh: '本体', en: 'Ontology', zhTW: '本體' },

  // Meetings page
  'meetings.hint': { zh: '团队会议与纪要', en: 'Team meetings & minutes', zhTW: '團隊會議與紀要' },
  'meetings.create': { zh: '发起会议', en: 'New Meeting', zhTW: '發起會議' },
  'meetings.createHint': { zh: '立即开一个新房间 →', en: 'Start a new room now →', zhTW: '立即開一個新房間 →' },
  'meetings.totalMeetings': { zh: '会议总数', en: 'Total Meetings', zhTW: '會議總數' },
  'meetings.hours': { zh: '小时', en: 'hours', zhTW: '小時' },
  'meetings.minutesCount': { zh: '份纪要', en: 'minutes', zhTW: '份紀要' },
  'meetings.searchPlaceholder': { zh: '搜索会议标题、参会者...', en: 'Search meetings...', zhTW: '搜尋會議...' },
  'meetings.records': { zh: '会议纪要', en: 'Meeting Records', zhTW: '會議紀要' },
  'meetings.loading': { zh: '加载中...', en: 'Loading...', zhTW: '載入中...' },
  'meetings.empty': { zh: '暂无会议', en: 'No meetings yet', zhTW: '暫無會議' },
  'meetings.emptyHint': { zh: '点击「发起会议」开始你的第一场会议', en: 'Click "New Meeting" to start your first meeting', zhTW: '點擊「發起會議」開始你的第一場會議' },

  // Memory page
  'memory.title': { zh: '共享知识库', en: 'Shared Knowledge', zhTW: '共享知識庫' },
  'memory.documents': { zh: '篇文档', en: 'documents', zhTW: '篇文件' },
  'memory.searchPlaceholder': { zh: '搜索知识库...', en: 'Search knowledge base...', zhTW: '搜尋知識庫...' },
  'memory.loading': { zh: '加载中...', en: 'Loading...', zhTW: '載入中...' },
  'memory.empty': { zh: '知识库为空', en: 'Knowledge base is empty', zhTW: '知識庫為空' },
  'memory.emptyHint': { zh: '上传 .md 文档作为所有 Agent 的共享规则与知识', en: 'Upload .md files as shared rules and knowledge for all agents', zhTW: '上傳 .md 文件作為所有 Agent 的共享規則與知識' },
  'memory.back': { zh: '返回', en: 'Back', zhTW: '返回' },
  'memory.desc': { zh: '共享知识库中的文档会被组织内所有 Agent 作为共同规则和记忆参考，仅支持 .md 格式', en: 'Documents here serve as shared rules and memory for all agents. Only .md files supported.', zhTW: '共享知識庫中的文件會被組織內所有 Agent 作為共同規則和記憶參考，僅支援 .md 格式' },
  'memory.upload': { zh: '上传文档', en: 'Upload', zhTW: '上傳文件' },
  'memory.deleteConfirm': { zh: '确定删除这篇文档吗？', en: 'Delete this document?', zhTW: '確定刪除這篇文件嗎？' },

  // Settings tabs
  'settings.title': { zh: '设置', en: 'Settings', zhTW: '設定' },
  'settings.general': { zh: '通用', en: 'General', zhTW: '一般' },
  'settings.appearance': { zh: '外观', en: 'Appearance', zhTW: '外觀' },
  'settings.auth': { zh: '账号', en: 'Account', zhTW: '帳號' },
  'settings.guide': { zh: '使用教程', en: 'Guide', zhTW: '使用教學' },
  'settings.about': { zh: '关于', en: 'About', zhTW: '關於' },

  // General
  'general.eyebrow': { zh: '偏好设置', en: 'Preferences', zhTW: '偏好設定' },
  'general.title': { zh: '通用设置', en: 'General', zhTW: '一般設定' },
  'general.desc': { zh: '管理你的个人偏好和组织设置。', en: 'Manage your preferences and organization settings.', zhTW: '管理你的個人偏好和組織設定。' },
  'general.displayName': { zh: '显示名称', en: 'Display name', zhTW: '顯示名稱' },
  'general.displayNamePlaceholder': { zh: '你的名字', en: 'Your name', zhTW: '你的名字' },
  'general.language': { zh: '语言', en: 'Language', zhTW: '語言' },
  'general.defaultWorkDir': { zh: '默认工作目录', en: 'Default working directory', zhTW: '預設工作目錄' },
  'general.defaultWorkDirHint': { zh: '新建 Agent 时的默认文件系统根目录', en: 'Default file system root when creating new agents', zhTW: '新建 Agent 時的預設檔案系統根目錄' },
  'general.chatHistory': { zh: '聊天记录', en: 'Chat History', zhTW: '聊天記錄' },
  'general.chatHistoryDesc': { zh: '清除所有本地聊天记录，此操作不可撤销。', en: 'Clear all local chat sessions. This cannot be undone.', zhTW: '清除所有本地聊天記錄，此操作不可撤銷。' },
  'general.clearSessions': { zh: '清除所有会话', en: 'Clear all sessions', zhTW: '清除所有對話' },
  'general.saved': { zh: '已保存', en: 'Saved', zhTW: '已儲存' },

  // User profile card
  'profile.open': { zh: '打开个人资料', en: 'Open profile', zhTW: '開啟個人資料' },
  'profile.close': { zh: '关闭个人资料', en: 'Close profile', zhTW: '關閉個人資料' },
  'profile.title': { zh: '个人资料', en: 'Profile', zhTW: '個人資料' },
  'profile.uploadAvatar': { zh: '上传新头像', en: 'Upload new avatar', zhTW: '上傳新頭像' },
  'profile.chooseAvatar': { zh: '选择头像', en: 'Choose an avatar', zhTW: '選擇頭像' },
  'profile.presetAvatar': { zh: '预设头像', en: 'Preset avatar', zhTW: '預設頭像' },
  'profile.imageTypeError': { zh: '仅支持 PNG、JPG 或 WebP 图片', en: 'Use a PNG, JPG, or WebP image', zhTW: '僅支援 PNG、JPG 或 WebP 圖片' },
  'profile.imageSizeError': { zh: '图片不能超过 2 MB', en: 'Image must be under 2 MB', zhTW: '圖片不能超過 2 MB' },
  'profile.imageReadError': { zh: '图片读取失败，请重试', en: 'Could not read the image. Try again.', zhTW: '圖片讀取失敗，請重試' },
  'profile.saveError': { zh: '保存失败，请稍后重试', en: 'Could not save. Try again later.', zhTW: '儲存失敗，請稍後重試' },

  // Auth
  'auth.eyebrow': { zh: '安全', en: 'Security', zhTW: '安全' },
  'auth.title': { zh: '账号管理', en: 'Account', zhTW: '帳號管理' },
  'auth.desc': { zh: '管理登录状态和账号设置。', en: 'Manage your session and account settings.', zhTW: '管理登入狀態和帳號設定。' },
  'auth.currentToken': { zh: '当前 Token', en: 'Current token', zhTW: '目前 Token' },
  'auth.reveal': { zh: '显示', en: 'Reveal', zhTW: '顯示' },
  'auth.hide': { zh: '隐藏', en: 'Hide', zhTW: '隱藏' },
  'auth.signOut': { zh: '退出登录', en: 'Sign out', zhTW: '登出' },
  'auth.signOutDesc': { zh: '退出后需要重新输入 Token 才能访问。', en: 'You will need to re-enter the token to access again.', zhTW: '登出後需要重新輸入 Token 才能存取。' },

  // About
  'about.eyebrow': { zh: '系统', en: 'System', zhTW: '系統' },
  'about.title': { zh: '关于', en: 'About', zhTW: '關於' },
  'about.desc': { zh: '关于此应用的信息。', en: 'Information about this application.', zhTW: '關於此應用程式的資訊。' },
  'about.description': {
    zh: '以 Codex 为底座的多通道 AI Agent 平台，连接 Web、飞书、Telegram 和微信。',
    en: 'A Codex-powered multi-channel AI agent platform for Web, Feishu, Telegram, and WeChat.',
    zhTW: '以 Codex 為底座的多通道 AI Agent 平台，連接 Web、飛書、Telegram 和微信。',
  },
  'about.guideTitle': { zh: '产品介绍', en: 'Product Guide', zhTW: '產品介紹' },
  'about.guideDesc': { zh: '了解 NovaX 的核心概念和使用方法', en: 'Learn about NovaX concepts and how to use the platform', zhTW: '了解 NovaX 的核心概念和使用方法' },
  'about.agreementTitle': { zh: '用户协议与隐私声明', en: 'User Agreement & Privacy', zhTW: '使用者協議與隱私聲明' },
  'about.agreementDesc': { zh: '数据处理、服务条款和免责声明', en: 'Data handling, terms of service, and disclaimers', zhTW: '資料處理、服務條款和免責聲明' },

  // Contacts
  'contacts.title': { zh: '你的团队', en: 'Your team', zhTW: '你的團隊' },
  'contacts.agents': { zh: '个 Agent', en: ' agent(s)', zhTW: '個 Agent' },
  'contacts.online': { zh: '在线', en: 'online', zhTW: '線上' },
  'contacts.newAgent': { zh: '+ 新建 Agent', en: '+ New agent', zhTW: '+ 新建 Agent' },
  'contacts.openChat': { zh: '对话', en: 'Open chat', zhTW: '對話' },
  'contacts.noAgents': { zh: '没有已配置的 Agent', en: 'No agents configured', zhTW: '沒有已設定的 Agent' },
  'contacts.connecting': { zh: '连接中...', en: 'Connecting...', zhTW: '連線中...' },

  // Create agent modal
  'create.title': { zh: '新建 Agent', en: 'Create New Agent', zhTW: '新建 Agent' },
  'create.name': { zh: '名称', en: 'Name', zhTW: '名稱' },
  'create.namePlaceholder': { zh: '例如 researcher', en: 'e.g. researcher', zhTW: '例如 researcher' },
  'create.description': { zh: '描述', en: 'Description', zhTW: '描述' },
  'create.descPlaceholder': { zh: '例如 研究助手', en: 'e.g. Research assistant', zhTW: '例如 研究助手' },
  'create.soul': { zh: 'Soul', en: 'Soul', zhTW: 'Soul' },
  'create.soulPlaceholder': { zh: '定义这个 Agent 的性格、指令和能力...', en: 'Define this agent\'s personality, instructions, and capabilities...', zhTW: '定義這個 Agent 的性格、指令和能力...' },
  'create.cancel': { zh: '取消', en: 'Cancel', zhTW: '取消' },
  'create.create': { zh: '创建', en: 'Create', zhTW: '建立' },
  'create.creating': { zh: '创建中...', en: 'Creating...', zhTW: '建立中...' },
  'create.nameRequired': { zh: '名称不能为空', en: 'Name is required', zhTW: '名稱不能為空' },

  // Agent detail modal
  'detail.changeAvatar': { zh: '更换头像', en: 'Change Avatar', zhTW: '更換頭像' },
  'detail.uploadImage': { zh: '上传图片', en: 'Upload Image', zhTW: '上傳圖片' },
  'detail.choosePreset': { zh: '或选择预设', en: 'OR CHOOSE A PRESET', zhTW: '或選擇預設' },
  'detail.soul': { zh: 'Soul', en: 'Soul', zhTW: 'Soul' },
  'detail.edit': { zh: '编辑', en: 'Edit', zhTW: '編輯' },
  'detail.save': { zh: '保存', en: 'Save', zhTW: '儲存' },
  'detail.saving': { zh: '保存中...', en: 'Saving...', zhTW: '儲存中...' },
  'detail.cancel': { zh: '取消', en: 'Cancel', zhTW: '取消' },
  'detail.noSoul': { zh: '未配置 Soul — 点击编辑创建', en: 'No soul configured — click Edit to create one', zhTW: '未設定 Soul — 點擊編輯建立' },
  'detail.deleteAgent': { zh: '删除 Agent', en: 'Delete Agent', zhTW: '刪除 Agent' },
  'detail.confirmDelete': { zh: '确定删除？此操作不可撤销。', en: 'Are you sure? This cannot be undone.', zhTW: '確定刪除？此操作不可撤銷。' },
  'detail.yesDelete': { zh: '确认删除', en: 'Yes, delete', zhTW: '確認刪除' },
  'detail.deleting': { zh: '删除中...', en: 'Deleting...', zhTW: '刪除中...' },

  // Chat
  'chat.conversations': { zh: '对话', en: 'Conversations', zhTW: '對話' },
  'chat.directMessages': { zh: '私信', en: 'Direct Messages', zhTW: '私訊' },
  'chat.search': { zh: '搜索对话...', en: 'Search conversations...', zhTW: '搜尋對話...' },
  'chat.workspace': { zh: '组织', en: 'Organization', zhTW: '組織' },
  'chat.groupAvatar': { zh: '全', en: 'All', zhTW: '全' },
  'chat.groupName': { zh: '全员群', en: 'Everyone', zhTW: '全員群' },
  'chat.groupDesc': { zh: '所有成员和 Agent 的群聊', en: 'Group chat with all members and agents', zhTW: '所有成員和 Agent 的群聊' },
  'chat.newGroup': { zh: '新建群组', en: 'New Group', zhTW: '新建群組' },
  'chat.createGroup': { zh: '创建群组', en: 'Create Group', zhTW: '建立群組' },
  'chat.groupNameLabel': { zh: '群名称', en: 'Group Name', zhTW: '群名稱' },
  'chat.groupNamePlaceholder': { zh: '输入群名称...', en: 'Enter group name...', zhTW: '輸入群名稱...' },
  'chat.selectMembers': { zh: '选择成员', en: 'Select Members', zhTW: '選擇成員' },
  'chat.minMembers': { zh: '至少选择 2 个成员', en: 'Select at least 2 members', zhTW: '至少選擇 2 個成員' },
  'chat.creating': { zh: '创建中...', en: 'Creating...', zhTW: '建立中...' },
  'chat.editGroup': { zh: '编辑群组', en: 'Edit Group', zhTW: '編輯群組' },
  'chat.deleteGroup': { zh: '删除群组', en: 'Delete Group', zhTW: '刪除群組' },
  'chat.deleteGroupConfirm': { zh: '确定删除这个群组吗？聊天记录将被清除。', en: 'Delete this group? Chat history will be cleared.', zhTW: '確定刪除這個群組嗎？聊天記錄將被清除。' },
  'chat.groups': { zh: '群组', en: 'Groups', zhTW: '群組' },

  // ChatPage tabs & top bar
  'chat.messages': { zh: '消息', en: 'Messages', zhTW: '訊息' },
  'chat.tasks': { zh: '任务', en: 'Tasks', zhTW: '任務' },
  'chat.skill': { zh: '技能', en: 'Skill', zhTW: '技能' },
  'skill.installed': { zh: '已安装', en: 'Installed', zhTW: '已安裝' },
  'skill.marketplace': { zh: '技能市场', en: 'Marketplace', zhTW: '技能市場' },
  'skill.official': { zh: '官方技能', en: 'Official', zhTW: '官方技能' },
  'skill.custom': { zh: '自定义', en: 'Custom', zhTW: '自訂' },
  'skill.install': { zh: '安装', en: 'Install', zhTW: '安裝' },
  'skill.installing': { zh: '安装中...', en: 'Installing...', zhTW: '安裝中...' },
  'skill.installed_badge': { zh: '已安装', en: 'Installed', zhTW: '已安裝' },
  'skill.uninstall': { zh: '卸载', en: 'Uninstall', zhTW: '解除安裝' },
  'skill.noSkills': { zh: '暂无技能', en: 'No skills', zhTW: '暫無技能' },
  'skill.noSkillsHint': { zh: '从技能市场安装技能到此 Agent', en: 'Install skills from the marketplace', zhTW: '從技能市場安裝技能到此 Agent' },
  'skill.marketplaceEmpty': { zh: '暂无可用技能', en: 'No skills available', zhTW: '暫無可用技能' },
  'skill.invocable': { zh: '可调用', en: 'Invocable', zhTW: '可呼叫' },
  'skill.back': { zh: '返回', en: 'Back', zhTW: '返回' },
  'skill.execMode': { zh: '执行模式', en: 'Exec Mode', zhTW: '執行模式' },
  'skill.allowedTools': { zh: '可用工具', en: 'Allowed Tools', zhTW: '可用工具' },
  'chat.memory': { zh: '记忆', en: 'Memory', zhTW: '記憶' },
  'chat.files': { zh: '文件', en: 'Files', zhTW: '檔案' },
  'chat.online': { zh: '在线', en: 'ONLINE', zhTW: '線上' },
  'chat.offline': { zh: '离线', en: 'OFFLINE', zhTW: '離線' },
  'chat.research': { zh: '研究', en: 'RESEARCH', zhTW: '研究' },
  'chat.noAgents': { zh: '没有已配置的 Agent', en: 'No agents configured', zhTW: '沒有已設定的 Agent' },
  'chat.connecting': { zh: '连接中...', en: 'Connecting...', zhTW: '連線中...' },
  'chat.selectAgent': { zh: '选择一个 Agent 开始对话', en: 'Select an agent to start a conversation', zhTW: '選擇一個 Agent 開始對話' },
  'chat.startConversation': { zh: '开始对话', en: 'Start a conversation', zhTW: '開始對話' },

  // Contacts card
  'contacts.capabilities': { zh: '能力', en: 'CAPABILITIES', zhTW: '能力' },
  'contacts.busy': { zh: '忙碌', en: 'BUSY', zhTW: '忙碌' },
  'contacts.statusOnline': { zh: '在线', en: 'ONLINE', zhTW: '線上' },
  'contacts.statusOffline': { zh: '离线', en: 'OFFLINE', zhTW: '離線' },

  // Team / Members
  'team.members': { zh: '成员', en: 'Members', zhTW: '成員' },
  'team.agents': { zh: 'Agents', en: 'Agents', zhTW: 'Agents' },
  'team.invite': { zh: '邀请成员', en: 'Invite', zhTW: '邀請成員' },
  'team.inviteTitle': { zh: '邀请成员', en: 'Invite Member', zhTW: '邀請成員' },
  'team.inviteDesc': { zh: '输入邮箱邀请成员加入组织', en: 'Enter email to invite a member to this organization', zhTW: '輸入信箱邀請成員加入組織' },
  'team.email': { zh: '邮箱', en: 'Email', zhTW: '信箱' },
  'team.emailPlaceholder': { zh: '输入邮箱地址', en: 'Enter email address', zhTW: '輸入信箱地址' },
  'team.role': { zh: '角色', en: 'Role', zhTW: '角色' },
  'team.send': { zh: '发送邀请', en: 'Send Invite', zhTW: '傳送邀請' },
  'team.sending': { zh: '发送中...', en: 'Sending...', zhTW: '傳送中...' },
  'team.sent': { zh: '邀请已发送', en: 'Invite sent', zhTW: '邀請已傳送' },
  'team.cancel': { zh: '取消', en: 'Cancel', zhTW: '取消' },
  'team.owner': { zh: '所有者', en: 'Owner', zhTW: '擁有者' },
  'team.admin': { zh: '管理员', en: 'Admin', zhTW: '管理員' },
  'team.member': { zh: '成员', en: 'Member', zhTW: '成員' },
  'team.remove': { zh: '移除', en: 'Remove', zhTW: '移除' },
  'team.pendingInvites': { zh: '待处理邀请', en: 'Pending Invites', zhTW: '待處理邀請' },
  'team.revoke': { zh: '撤销', en: 'Revoke', zhTW: '撤銷' },
  'team.stats': { zh: '统计', en: 'Statistics', zhTW: '統計' },
  'team.noMembers': { zh: '暂无其他成员', en: 'No other members yet', zhTW: '暫無其他成員' },
  'team.noPending': { zh: '没有待处理的邀请', en: 'No pending invites', zhTW: '沒有待處理的邀請' },
  'team.switchWorkspace': { zh: '切换组织', en: 'Switch Organization', zhTW: '切換組織' },

  // MessageList
  'msg.thinking': { zh: '思考中...', en: 'Thinking...', zhTW: '思考中...' },
  'msg.running': { zh: '运行中', en: 'Running', zhTW: '執行中' },
  'msg.error': { zh: '发生错误', en: 'An error occurred', zhTW: '發生錯誤' },
  'msg.toolCalls': { zh: '个工具调用已完成', en: ' tool call(s) completed', zhTW: '個工具呼叫已完成' },

  // Time
  'time.justNow': { zh: '刚刚', en: 'Just now', zhTW: '剛剛' },
  'time.minutesAgo': { zh: '分钟前', en: 'm ago', zhTW: '分鐘前' },
  'time.hoursAgo': { zh: '小时前', en: 'h ago', zhTW: '小時前' },
};

export function useT() {
  const [lang] = useLang();
  return (key: string): string => {
    const entry = translations[key];
    if (!entry) return key;
    if (lang === 'zh' || lang === 'zhTW' || lang === 'en') return entry[lang] || entry.en || key;
    return extraTranslations[lang]?.[key] || entry.en || key;
  };
}
